import * as Sentry from '@sentry/react';
import type { PaymentQuery } from '../utils/query';
import type { OperationStatusResponse } from '../universal-flow/types';

export type ApiClientOptions = {
  basePath?: string;
  fetchImpl?: typeof fetch;
};

/**
 * Проверяет, что строка не пустая (не undefined, не null, не пустая строка)
 */
function isNonEmptyString(value: string | undefined | null): value is string {
  return value !== undefined && value !== null && value !== '';
}

function getApiBaseUrl(): string {
  const backendDomain = import.meta.env.BACKEND_DOMAIN;
  
  if (isNonEmptyString(backendDomain)) {
    // Remove trailing slashes and ensure protocol
    const domain = backendDomain.trim().replace(/\/+$/, '');
    const url = domain.startsWith('http://') || domain.startsWith('https://') 
      ? domain 
      : `https://${domain}`;
    return `${url}/api`;
  }
  
  // Fallback to relative path for local development
  return '/api';
}

export class ApiClient {
  private readonly basePath: string;
  private readonly fetchImpl: typeof fetch;
  private readonly encryptedPayload?: string;
  private readonly merchantId?: string;
  private orderId?: string;

  constructor(query: PaymentQuery, options: ApiClientOptions = {}) {
    this.basePath = options.basePath ?? getApiBaseUrl();
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);

    this.orderId = query.orderID ?? undefined;
    this.encryptedPayload = query.payload ?? undefined;
    this.merchantId = query.merchantId ?? undefined;
  }

  public setOrderIdentifiers(params: { order_id?: string }): void {
    if (isNonEmptyString(params.order_id)) {
      this.orderId = params.order_id;
    }
  }

  public async loadStatus(extra?: Record<string, unknown>): Promise<OperationStatusResponse> {
    this.assertCredentials();
    const payload = this.buildRequestPayload(extra);

    const response = await this.postJson<OperationStatusResponse>(this.resolveStatusUrl(), payload);

    if (isNonEmptyString(response.order_id)) {
      this.orderId = response.order_id;
    }

    return response;
  }

  private buildRequestPayload(extra?: Record<string, unknown>): Record<string, unknown> {
    const payload: Record<string, unknown> = {};

    if (isNonEmptyString(this.orderId)) {
      payload.order_id = this.orderId;
    }

    if (extra !== undefined && extra !== null && Object.keys(extra).length > 0) {
      payload.extra = extra;
    }

    if (isNonEmptyString(this.merchantId)) {
      payload.merchant_id = this.merchantId;
    }

    if (isNonEmptyString(this.encryptedPayload)) {
      payload.payload = this.encryptedPayload;
    }

    return payload;
  }

  private assertCredentials(): void {
    if (!isNonEmptyString(this.merchantId)) {
      throw new Error('URL is missing merchant identifier');
    }

    if (!isNonEmptyString(this.encryptedPayload)) {
      throw new Error('URL is missing encrypted payload');
    }
  }

  private async postJson<T>(url: string, payload: unknown): Promise<T> {
    let res: Response;
    try {
      res = await this.fetchImpl(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (networkError) {
      Sentry.withScope((scope) => {
        scope.setContext('request', {
          url,
          method: 'POST',
          failure: 'network_or_cors'
        });
        scope.setTag('report_source', 'manual');
        Sentry.captureException(networkError);
      });
      throw networkError;
    }

    if (!res.ok) {
      const message = await safeReadText(res);
      const err = new Error(`Payment service request failed (${res.status}): ${message}`);
      Sentry.withScope((scope) => {
        scope.setTag('report_source', 'manual');
        scope.setTag('http_status', String(res.status));
        scope.setContext('request', { url, method: 'POST' });
        scope.setContext('response', {
          status: res.status,
          statusText: res.statusText,
          body: message
        });
        Sentry.captureException(err);
      });
      throw err;
    }

    return (await res.json()) as T;
  }

  private resolveStatusUrl(): string {
    return `${this.basePath.replace(/\/+$/, '')}/status`;
  }
}

async function safeReadText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '<no body>';
  }
}

